import userEventService from './user-event-service';
import userService from './user-service';
import { PROXY_EVENT_TYPES, PROXY_EVENT_CATEGORY } from './types/proxy-event-types';

/**
 * Service for tracking proxy profile events
 * Leverages existing user-event-service infrastructure
 * @class ProxyEventTrackingService
 */
class ProxyEventTrackingService {
  /**
   * Track when a user activates a proxy profile
   * @param {string} proxyUserId - ID of the activated proxy user
   */
  static async trackProxyActivation(proxyUserId) {
    try {
      const user = await userService.getUser();
      const eventData = {
        eventCategory: PROXY_EVENT_CATEGORY,
        eventAction: PROXY_EVENT_TYPES.ACTIVATION,
        eventLabel: `user_${user.id}_activated_proxy_${proxyUserId}`,
        eventData: {
          user_id: user.id,
          proxy_user_id: proxyUserId,
          timestamp: new Date().toISOString(),
          event_type: PROXY_EVENT_TYPES.ACTIVATION,
        },
      };

      userEventService.trackEvent(eventData);
    } catch (error) {
      console.error('Failed to track proxy activation');
      // Don't throw - tracking failures shouldn't disrupt user workflow
    }
  }

  /**
   * Track when a user switches from one proxy profile to another
   * @param {string} previousProxyUserId - ID of the previous proxy user
   * @param {string} newProxyUserId - ID of the new proxy user
   */
  static async trackProxySwitch(previousProxyUserId, newProxyUserId) {
    try {
      const user = await userService.getUser();
      const eventData = {
        eventCategory: PROXY_EVENT_CATEGORY,
        eventAction: PROXY_EVENT_TYPES.SWITCH,
        eventLabel: `user_${user.id}_switched_from_${previousProxyUserId}_to_${newProxyUserId}`,
        eventData: {
          user_id: user.id,
          previous_proxy_user_id: previousProxyUserId,
          new_proxy_user_id: newProxyUserId,
          timestamp: new Date().toISOString(),
          event_type: PROXY_EVENT_TYPES.SWITCH,
        },
      };

      userEventService.trackEvent(eventData);
    } catch (error) {
      console.error('Failed to track proxy switch');
      // Don't throw - tracking failures shouldn't disrupt user workflow
    }
  }

  /**
   * Track when a user exits a proxy profile
   * @param {string} proxyUserId - ID of the exited proxy user
   */
  static async trackProxyExit(proxyUserId) {
    try {
      const user = await userService.getUser();
      const eventData = {
        eventCategory: PROXY_EVENT_CATEGORY,
        eventAction: PROXY_EVENT_TYPES.EXIT,
        eventLabel: `user_${user.id}_exited_proxy_${proxyUserId}`,
        eventData: {
          user_id: user.id,
          proxy_user_id: proxyUserId,
          timestamp: new Date().toISOString(),
          event_type: PROXY_EVENT_TYPES.EXIT,
        },
      };

      userEventService.trackEvent(eventData);
    } catch (error) {
      console.error('Failed to track proxy exit');
      // Don't throw - tracking failures shouldn't disrupt user workflow
    }
  }

  /**
   * Track when a user grants permission to a proxy profile
   * @param {string} proxyUserId - ID of the proxy user granted permission
   */
  static async trackProxyAccessGranted(proxyUserId) {
    try {
      const user = await userService.getUser();
      const eventData = {
        eventCategory: PROXY_EVENT_CATEGORY,
        eventAction: PROXY_EVENT_TYPES.ACCESS_GRANTED,
        eventLabel: `user_${user.id}_granted_access_to_proxy_${proxyUserId}`,
        eventData: {
          user_id: user.id,
          proxy_user_id: proxyUserId,
          timestamp: new Date().toISOString(),
          event_type: PROXY_EVENT_TYPES.ACCESS_GRANTED,
        },
      };

      userEventService.trackEvent(eventData);
    } catch (error) {
      console.error('Failed to track proxy access granted');
      // Don't throw - tracking failures shouldn't disrupt user workflow
    }
  }

  /**
   * Track when a user revokes permission from a proxy profile
   * @param {string} proxyUserId - ID of the proxy user with revoked permission
   */
  static async trackProxyAccessRevoked(proxyUserId) {
    try {
      const user = await userService.getUser();
      const eventData = {
        eventCategory: PROXY_EVENT_CATEGORY,
        eventAction: PROXY_EVENT_TYPES.ACCESS_REVOKED,
        eventLabel: `user_${user.id}_revoked_access_from_proxy_${proxyUserId}`,
        eventData: {
          user_id: user.id,
          proxy_user_id: proxyUserId,
          timestamp: new Date().toISOString(),
          event_type: PROXY_EVENT_TYPES.ACCESS_REVOKED,
        },
      };

      userEventService.trackEvent(eventData);
    } catch (error) {
      console.error('Failed to track proxy access revoked');
      // Don't throw - tracking failures shouldn't disrupt user workflow
    }
  }

  /**
   * Track when proxy user feature flag is assigned or unassigned
   * @param {Object} flagData - Flag assignment data
   * @param {string} flagData.flag - Name of the feature flag
   * @param {string} flagData.action - 'ASSIGNED' or 'UNASSIGNED'
   * @param {string} flagData.timestamp - ISO timestamp of the event
   */
  static async trackFeatureFlagAssignment(flagData) {
    try {
      const user = await userService.getUser();
      const eventType = flagData.action === 'ASSIGNED'
        ? PROXY_EVENT_TYPES.FEATURE_FLAG_ASSIGNED
        : PROXY_EVENT_TYPES.FEATURE_FLAG_UNASSIGNED;

      const eventData = {
        eventCategory: PROXY_EVENT_CATEGORY,
        eventAction: eventType,
        eventLabel: `user_${user.id}_flag_${flagData.flag}_${flagData.action.toLowerCase()}`,
        eventData: {
          user_id: user.id,
          feature_flag: flagData.flag,
          action: flagData.action,
          timestamp: flagData.timestamp,
          event_type: eventType,
        },
      };

      userEventService.trackEvent(eventData);
      console.log('Proxy feature flag assignment tracked:', eventData);
    } catch (error) {
      console.error('Failed to track proxy feature flag assignment:');
      // Don't throw - tracking failures shouldn't disrupt user workflow
    }
  }
}

// Export class with static methods
export default ProxyEventTrackingService;
